import { useCallback, useEffect } from 'react';
import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Crypto from 'expo-crypto';
import { makeRedirectUri } from 'expo-auth-session';
import { supabase } from '../services/supabase';
import {
  ENUMERATION_CODES,
  isUserCancellation,
  oauthReturnErrorKey,
  resolveAuthError,
} from '../utils/authErrors';
import { capturerIncident } from '../services/sentry';
import { requestAccountDeletion } from '../services/account';
import { viderFileCollecte } from '../services/collecte';
import { VERSION_CONSENTEMENT } from '../config/terms';
import { useUserStore } from '../store/userStore';

export interface AuthResult {
  // CLÉ i18n, jamais un message. Le type porte la garantie : aucune chaîne du serveur ne peut
  // atteindre un écran, puisqu'il n'y a plus de champ où la mettre. Les écrans rendent la clé
  // avec `authErrorText`.
  errorKey?: string;
  // Inscription sans session : email de confirmation à venir (nouveau compte) OU adresse déjà
  // utilisée (réponse anti-énumération identique). Même retour dans les deux cas → rien n'est révélé.
  pendingConfirmation?: boolean;
}

// Retour OAuth dans l'app via le scheme custom (liste blanche Supabase : hormeia://auth/callback).
const OAUTH_REDIRECT = makeRedirectUri({ scheme: 'hormeia', path: 'auth/callback' });
// Lien de confirmation d'email : scheme natif EN DUR (pas makeRedirectUri). En dev build,
// makeRedirectUri renvoie l'URL du dev server (exp://…) qui n'est pas whitelistée côté Supabase →
// le lien retombe sur le Site URL. Une constante garantit hormeia://auth/confirm partout, sans
// détection d'environnement. Doit rester dans la liste blanche Supabase (Redirect URLs).
/**
 * Cible du lien de confirmation d'email : une page WEB, plus un schéma d'URL personnalisé.
 *
 * `hormeia://auth/confirm` ne fonctionnait que sur téléphone. Ouvert sur un ordinateur, le
 * navigateur ne savait qu'en faire : page blanche. Le compte était pourtant confirmé — l'endpoint
 * `/auth/v1/verify` de Supabase le fait AVANT de rediriger — mais rien ne le disait.
 *
 * La page publiée par le pipeline Netlify (`scripts/buildLegalDocs.js`) dit que le compte est
 * confirmé, puis passe la main à l'application sur mobile en conservant le `code`. Le trajet
 * téléphone est donc inchangé du point de vue de l'utilisateur : une redirection de plus, invisible.
 *
 * ⚠️ Cette URL doit figurer À L'IDENTIQUE dans les Redirect URLs de la console Supabase, qui compare
 * des chaînes exactes. Sans elle, Supabase retombe silencieusement sur la Site URL.
 */
const CONFIRM_REDIRECT = 'https://legal.alternjob.com/confirmation';

/**
 * Cible du lien de RÉINITIALISATION du mot de passe : même principe que la confirmation (page web
 * qui passe la main à l'application sur téléphone, sans rien dire de faux sur un ordinateur), mais
 * une page et un chemin d'application distincts (`hormeia://auth/reset`) pour que les liens
 * expirés ou ouverts sur un autre appareil reçoivent le bon conseil.
 *
 * ⚠️ Doit figurer À L'IDENTIQUE dans les Redirect URLs de la console Supabase (comparaison de
 * chaînes exactes) ; sinon Supabase retombe silencieusement sur la Site URL.
 */
const RESET_REDIRECT = 'https://legal.alternjob.com/reinitialisation';

/**
 * Gère l'authentification email/password + Google OAuth (flux navigateur PKCE) et
 * synchronise la session avec le userStore. Apple reste à brancher.
 */
export function useAuth() {
  const setSession = useUserStore((state) => state.setSession);
  const setRecoveryPending = useUserStore((state) => state.setRecoveryPending);
  const reset = useUserStore((state) => state.reset);

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => setSession(data.session))
      .catch(() => undefined);

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // AVANT la session : l'arbre de navigation lit les deux, et poser la session seule ferait
      // passer l'application par l'écran d'accueil avant de la ramener sur « nouveau mot de passe ».
      if (event === 'PASSWORD_RECOVERY') setRecoveryPending(true);
      // Plus de session (déconnexion, jeton révoqué ou expiré, rafraîchissement refusé) : un verrou
      // resté posé s'appliquerait à la PROCHAINE connexion, même à un autre compte, et lui ferait
      // changer son mot de passe sans l'avoir demandé.
      else if (!session) setRecoveryPending(false);
      setSession(session);
    });

    return () => data.subscription.unsubscribe();
  }, [setSession, setRecoveryPending]);

  const signInEmail = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return error ? { errorKey: resolveAuthError(error, 'signin') } : {};
    } catch (error) {
      return { errorKey: resolveAuthError(error, 'signin') };
    }
  }, []);

  const signUpEmail = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      // L'inscription email n'est atteignable qu'après acceptation des CGU (case obligatoire) →
      // on enregistre le consentement via les métadonnées (le trigger handle_new_user le pose).
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { terms_version: VERSION_CONSENTEMENT }, emailRedirectTo: CONFIRM_REDIRECT },
      });
      // Adresse déjà prise → exactement le retour d'une inscription réussie (cf. ENUMERATION_CODES).
      if (error && ENUMERATION_CODES.has(error.code ?? '')) return { pendingConfirmation: true };
      if (error) return { errorKey: resolveAuthError(error, 'signup') };
      // Session présente = login immédiat (confirmation désactivée). Sinon, un email de confirmation
      // part (nouveau compte) ou la réponse est maquillée (adresse déjà prise) → même message neutre.
      return data.session ? {} : { pendingConfirmation: true };
    } catch (error) {
      return { errorKey: resolveAuthError(error, 'signup') };
    }
  }, []);

  /**
   * Demande d'un lien de réinitialisation. Supabase répond sans erreur que le compte existe ou
   * non, et l'écran affiche le même message dans les deux cas. LIMITE CONNUE : une SECONDE demande
   * rapprochée pour un compte existant reçoit `over_email_send_rate_limit`, ce qu'un compte inconnu
   * ne provoque pas. L'écran distingue donc, à ce moment-là, les deux cas pour quelqu'un qui
   * enchaîne les demandes ; la vraie parade (captcha ou fonction serveur) n'est pas dans l'app.
   */
  const requestPasswordReset = useCallback(async (email: string): Promise<AuthResult> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: RESET_REDIRECT });
      return error ? { errorKey: resolveAuthError(error, 'reset') } : {};
    } catch (error) {
      return { errorKey: resolveAuthError(error, 'reset') };
    }
  }, []);

  /**
   * Enregistre le nouveau mot de passe de la session de récupération, puis lève le verrou de
   * l'écran. Le verrou ne tombe QU'APRÈS un succès : un échec laisse la personne sur l'écran.
   */
  const updatePassword = useCallback(
    async (password: string): Promise<AuthResult> => {
      try {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) return { errorKey: resolveAuthError(error, 'reset') };
        setRecoveryPending(false);
        // Changer de mot de passe ne coupe pas les AUTRES sessions : or on réinitialise souvent
        // parce que le compte est compromis. Best-effort : un échec ici ne doit pas faire croire
        // que le mot de passe n'a pas été changé.
        try {
          await supabase.auth.signOut({ scope: 'others' });
        } catch {
          // Volontairement muet.
        }
        return {};
      } catch (error) {
        return { errorKey: resolveAuthError(error, 'reset') };
      }
    },
    [setRecoveryPending],
  );

  // Google OAuth : flux navigateur PKCE. On ouvre l'URL Supabase, puis on échange le `code`
  // du retour contre une session. onAuthStateChange (ci-dessus) bascule l'app en connecté.
  const signInWithGoogle = useCallback(async (): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: OAUTH_REDIRECT, skipBrowserRedirect: true },
      });
      if (error) return { errorKey: resolveAuthError(error, 'oauth') };
      if (!data?.url) return { errorKey: 'auth.error.googleStart' };

      const result = await WebBrowser.openAuthSessionAsync(data.url, OAUTH_REDIRECT);
      // Fermeture / annulation par l'utilisateur → pas d'erreur affichée.
      if (result.type !== 'success') return {};

      const { queryParams } = Linking.parse(result.url);
      // Vocabulaire OAuth (RFC 6749), pas Supabase : trié à part, et jamais affiché tel quel.
      // `null` = refus du consentement, donc une annulation : on ne montre rien.
      if (queryParams?.error) {
        const errorKey = oauthReturnErrorKey(String(queryParams.error));
        return errorKey ? { errorKey } : {};
      }
      const code = queryParams?.code;
      if (typeof code !== 'string' || !code) return { errorKey: 'auth.error.googleResponse' };

      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      return exchangeError ? { errorKey: resolveAuthError(exchangeError, 'oauth') } : {};
    } catch (error) {
      return { errorKey: resolveAuthError(error, 'oauth') };
    }
  }, []);

  // Sign in with Apple : flux natif iOS. Nonce hashé (SHA-256) passé à Apple, nonce brut à
  // Supabase qui re-hashe et compare (anti-rejeu). onAuthStateChange bascule l'app en connecté.
  const signInWithApple = useCallback(async (): Promise<AuthResult> => {
    try {
      const rawNonce = Crypto.randomUUID();
      const hashedNonce = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, rawNonce);

      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
        nonce: hashedNonce,
      });
      if (!credential.identityToken) return { errorKey: 'auth.error.appleResponse' };

      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: credential.identityToken,
        nonce: rawNonce,
      });
      return error ? { errorKey: resolveAuthError(error, 'oauth') } : {};
    } catch (error) {
      if (isUserCancellation(error)) return {};
      return { errorKey: resolveAuthError(error, 'oauth') };
    }
  }, []);

  const signOut = useCallback(async (): Promise<AuthResult> => {
    try {
      const { error } = await supabase.auth.signOut();
      reset();
      void viderFileCollecte();
      return error ? { errorKey: resolveAuthError(error, 'signout') } : {};
    } catch (error) {
      return { errorKey: resolveAuthError(error, 'signout') };
    }
  }, [reset]);

  // Suppression définitive du compte : l'edge function purge Storage + auth (→ cascade DB),
  // puis on efface la session locale → onAuthStateChange ramène à l'écran d'auth.
  const deleteAccount = useCallback(async (): Promise<AuthResult> => {
    // Compte « Sign in with Apple » sur iOS : un code d'autorisation, demandé à Apple maintenant, permet
    // au serveur de révoquer le jeton Apple (exigence App Store). Une annulation volontaire de la feuille
    // arrête la suppression (rien n'est détruit, on peut réessayer) ; toute autre erreur d'Apple ne
    // retient pas le compte : il est supprimé sans révocation, et le serveur le journalise.
    let codeApple: string | undefined;
    const fournisseurs = useUserStore.getState().session?.user?.app_metadata?.providers;
    if (Platform.OS === 'ios' && Array.isArray(fournisseurs) && fournisseurs.includes('apple')) {
      try {
        const credential = await AppleAuthentication.signInAsync({ requestedScopes: [] });
        codeApple = credential.authorizationCode ?? undefined;
      } catch (e) {
        if ((e as { code?: string }).code === 'ERR_REQUEST_CANCELED') return { errorKey: 'auth.error.deleteAppleCancelled' };
      }
    }
    try {
      await requestAccountDeletion(codeApple);
      await supabase.auth.signOut();
      reset();
      void viderFileCollecte();
      return {};
    } catch (error) {
      // Pas une erreur Supabase Auth (l'edge function a échoué) : elle n'a donc pas de code, et le
      // libellé est fixe. Il dit que RIEN n'a été supprimé — le filet de restauration est posé
      // avant toute destruction, cf. services/account.ts.
      console.warn('[auth]', 'delete', 'edge_function', error instanceof Error ? error.message : String(error));
      // REMONTÉ MÊME SI CE N'EST PAS UNE ERREUR D'AUTHENTIFICATION. Une suppression de compte qui
      // échoue en silence est un manquement RGPD : il faut l'apprendre le jour même, et pas par
      // un utilisateur en colère quinze jours plus tard.
      capturerIncident('delete_account_failed', 'delete_account');
      return { errorKey: 'auth.error.deleteFailed' };
    }
  }, [reset]);

  return {
    signInEmail,
    signUpEmail,
    requestPasswordReset,
    updatePassword,
    signInWithGoogle,
    signInWithApple,
    signOut,
    deleteAccount,
  };
}
